"use client";

import dynamic from "next/dynamic";
import { useMemo, useState, type KeyboardEvent } from "react";
import NextImage from "next/image";
import {
  NextImage as JSSImage,
  Text,
  type LinkField,
} from "@sitecore-content-sdk/nextjs";
import { SitecoreLink as Link } from "../../atoms/SitecoreLink";
import Button from "../../atoms/Button";
import VideoIframe from "../../atoms/VideoIframe";
import RichTextWithTable from "../../molecules/RichTextWithTable/RichTextWithTables";
import {
  extractHiddenBlock,
  getConfiguredHeadingStyles,
} from "../../utils/styles";
import { CardProps } from "./card.props";

const CardVideoModal = dynamic(() => import("./CardVideoModal"));

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const computeButtonType = (
  field: LinkField | undefined,
  defaultType: string,
  isAllowReverseStyling: boolean,
  isDarkBg: boolean,
): string => {
  const explicit = field?.value?.class;
  if (isAllowReverseStyling) {
    return explicit || (isDarkBg ? `${defaultType}-reverse` : defaultType);
  }
  return field?.value?.class || "tertiary";
};

interface CtaButtonOpts {
  isPageEditing: boolean;
  isAllowReverseStyling: boolean;
  isVertical: boolean;
  isDarkBg: boolean;
}

const renderCTAButton = (
  field: LinkField | undefined,
  defaultType: string,
  opts: CtaButtonOpts,
) => {
  const { isPageEditing, isAllowReverseStyling, isVertical, isDarkBg } = opts;
  const href = field?.value?.href;
  if (!isPageEditing && !href) return null;
  let btnClasses = "";

  if (defaultType === "primary") {
    btnClasses = cx(
      isAllowReverseStyling ? "!mb-0" : "no-reverse",
      !isAllowReverseStyling && !field?.value?.class
        ? "!min-w-0 min-h-auto w-fit !mb-1 !leading-[28px] lg:!leading-[24px] tracking-[0.46px] hover:!text-ecolab-blue active:!text-ecolab-blue active:!underline"
        : "",
      !isVertical && "max-w-full",
    );
  } else if (defaultType === "secondary") {
    btnClasses = cx(
      !isAllowReverseStyling && "no-reverse",
      !isAllowReverseStyling && !field?.value?.class
        ? "!min-w-0 min-h-auto w-fit !leading-[28px] lg:!leading-[24px] tracking-[0.46px] hover:!text-ecolab-blue active:!text-ecolab-blue active:!underline"
        : "",
      !isVertical && "max-w-full",
    );
  }

  return (
    <Button
      field={field}
      buttonType={computeButtonType(
        field,
        defaultType,
        isAllowReverseStyling,
        isDarkBg,
      )}
      iconName="CHEVRON_BLUE_RIGHT"
      className={btnClasses}
      data-role="link"
    />
  );
};

const CardDefaultComponent = (props: CardProps) => (
  <div
    className={`component card ${props?.params?.Styles ?? ""} ${props?.fields ? "" : "emptyFields"}`}
  >
    <div className="component-content">
      <span className="is-empty-hint">Card</span>
    </div>
  </div>
);

export const Default = (props: CardProps) => {
  console.log("---->[Card] Component", props.fields);

  const { page, fields, params } = props;

  const isPageEditing = page?.mode?.isEditing ?? false;
  const isPreviewMode = page?.mode?.isPreview ?? false;

  const isVertical =
    params?.isVerticleAlignment === "true" || params?.VerticalCard === "1";

  const isDarkBg =
    params?.isDarkBg === "true" || params?.Styles?.includes("text-white");

  const isDarkBgInVerticalAlignment = isVertical && isDarkBg;

  const editModeBasedClasses = isPageEditing ? "" : "[&:empty]:hidden";

  const id = params?.RenderingIdentifier;
  const datasource = fields?.data?.datasource;
  const isPartOfCardStack = params?.isPartOfCardStack === "true";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const wrapperClass = useMemo(
    () =>
      cx(
        "overflow-hidden basis-full",
        isVertical
          ? "bg-transparent"
          : cx(
              "cardstack-card",
              "bg-white shadow-[var(--shadow-card)] rounded-lg",
              !params?.Styles?.includes("min-w-auto") && "min-w-[250px]",
            ),
        params?.Styles,
        hiddenBlockStyles,
        !isPartOfCardStack && isVertical && "px-4",
        !isPartOfCardStack && !isVertical && "mx-4",
      ),
    [isVertical, params?.Styles, hiddenBlockStyles, isPartOfCardStack],
  );

  if (!datasource) return <CardDefaultComponent {...props} />;

  const hasVideo = !!datasource?.YoutubeVideoUrl?.jsonValue?.value;
  const hasImage = !!datasource?.Image?.jsonValue?.value?.src;
  const showVideo = !isPageEditing && hasVideo;
  const showImage = !isPageEditing && !hasVideo && hasImage;
  const isDoNotStretchCardImage = params?.DoNotStretchCardImage === "true";
  const imageSrc = datasource?.Image?.jsonValue?.value?.src;
  const imageAlt = (datasource?.Image?.jsonValue?.value?.alt as string) ?? "";
  const contentWrapperClass = cx(
    "flex flex-col h-full pb-5",
    isVertical && "flex-col-reverse md:flex-row-reverse md:py-5 md:gap-x-5",
  );

  const mediaWrapperClass = cx(
    editModeBasedClasses,
    isVertical && "md:max-w-1/3 w-full",
    !isVertical &&
      showImage &&
      !isDoNotStretchCardImage &&
      "relative w-full overflow-hidden",
    isPageEditing && !isDarkBgInVerticalAlignment && "text-text-gray",
  );

  const textAreaClass = cx(
    isVertical
      ? cx((hasVideo || hasImage) && "md:max-w-2/3")
      : "pt-5 px-5 justify-between",
    "flex flex-col gap-y-6 flex-grow-1",
  );

  const ctaContainerClass = cx(
    isVertical
      ? "flex flex-wrap gap-y-4 items-start max-md:pb-6"
      : "flex flex-col items-start md:gap-y-4",
    isDarkBg && "dark-image-bg",
    editModeBasedClasses,
    isPageEditing && !isDarkBgInVerticalAlignment && "text-text-gray",
  );

  const pageEditingImageClass = cx(
    "block",
    isDoNotStretchCardImage
      ? "w-auto max-w-full h-auto mx-auto"
      : "w-full h-auto",
  );

  const doNotStretchImageClass = "block w-auto max-w-full h-auto mx-auto";

  const verticalImageClass = "block w-full h-auto";

  const renderMedia = () => {
    const shouldUseFixedImageBox =
      showImage && imageSrc && !isVertical && !isDoNotStretchCardImage;

    return (
      <div
        className={mediaWrapperClass}
        style={
          shouldUseFixedImageBox ? { aspectRatio: "440 / 248" } : undefined
        }
      >
        {isPageEditing && (
          <Text
            field={datasource.YoutubeVideoUrl.jsonValue}
            tag="p"
            className="mb-5"
          />
        )}

        {showVideo && (
          <VideoIframe
            src={datasource.YoutubeVideoUrl.jsonValue?.value?.toString() || ""}
            isPreviewMode={isPreviewMode}
          />
        )}

        {isPageEditing && (
          <JSSImage
            field={datasource?.Image?.jsonValue}
            className={pageEditingImageClass}
            width={440}
            height={248}
            imageParams={{ mw: 440, mh: 248 }}
          />
        )}

        {shouldUseFixedImageBox && (
          <NextImage
            src={imageSrc}
            alt={imageAlt}
            fill
            className="object-cover object-center"
            sizes="(max-width: 767px) 100vw, 440px"
          />
        )}

        {showImage && imageSrc && (isVertical || isDoNotStretchCardImage) && (
          <NextImage
            src={imageSrc}
            alt={imageAlt}
            className={
              isDoNotStretchCardImage
                ? doNotStretchImageClass
                : verticalImageClass
            }
            width={440}
            height={248}
            sizes="(max-width: 767px) 100vw, 440px"
          />
        )}
      </div>
    );
  };

  const renderContent = () => {
    const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
    const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase() || "h4";

    const titleClass = cx(
      `${
        typeOfTitleTag
          ? getConfiguredHeadingStyles(typeOfTitleTag)
          : "text-[24px] [&_*]:text-[24px] leading-[1.1] [&_*]:leading-[1.1]"
      } font-medium [&_*]:font-medium`,
      isVertical ? "md:[&:first-child]:mt-0" : "text-black",
      isDarkBgInVerticalAlignment && "text-white",
      !isDarkBg && isVertical && "text-ecolab-blue",
    );

    const subTitleClass = cx(
      `${getConfiguredHeadingStyles(typeOfSubtitleTag)} font-medium [&_*]:font-medium`,
      isDarkBgInVerticalAlignment && "text-white",
      !isVertical && "text-black",
    );

    const descriptionClass = cx(
      "richtext-content",
      isDarkBgInVerticalAlignment && "text-white",
      !isVertical && "text-text-gray",
    );

    return (
      <div>
        <RichTextWithTable
          field={datasource?.Title?.jsonValue}
          className={titleClass}
          tag={typeOfTitleTag || "h3"}
        />

        <RichTextWithTable
          field={datasource?.SubTitle?.jsonValue}
          className={subTitleClass}
          tag={typeOfSubtitleTag}
        />

        <RichTextWithTable
          field={datasource?.Description?.jsonValue}
          className={descriptionClass}
        />
      </div>
    );
  };

  return (
    <div
      id={id ?? undefined}
      className={wrapperClass}
      data-testid={`card-item-${params?.index ?? 0}`}
      data-role="card-component"
    >
      <div className={contentWrapperClass}>
        {renderMedia()}

        <div className={textAreaClass}>
          {renderContent()}

          <div className={ctaContainerClass}>
            {renderCTAButton(datasource?.PrimaryCTA?.jsonValue, "primary", {
              isPageEditing,
              isAllowReverseStyling: isVertical,
              isVertical,
              isDarkBg: isDarkBgInVerticalAlignment,
            })}

            {renderCTAButton(datasource?.SecondaryCTA?.jsonValue, "secondary", {
              isPageEditing,
              isAllowReverseStyling: isVertical,
              isVertical,
              isDarkBg: isDarkBgInVerticalAlignment,
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const McClarenCard = (props: CardProps) => {
  console.log("---->[McClarenCard] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;
  const isPreviewMode = page?.mode?.isPreview;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isPartOfCardStack = params?.isPartOfCardStack == "true";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const isVertical =
    params?.isVerticleAlignment === "true" || params?.VerticalCard === "1";
  const isDarkBg =
    params?.isDarkBg === "true" || params?.Styles?.includes("text-white");

  const id = params?.RenderingIdentifier;
  const datasource = fields?.data?.datasource;

  const wrapperClass = useMemo(
    () =>
      cx(
        "overflow-hidden basis-full",
        isVertical
          ? "bg-transparent"
          : cx(
              "cardstack-card",
              !params?.Styles?.includes("min-w-auto") && "min-w-[250px]",
            ),
        params?.Styles,
        hiddenBlockStyles,
        !isPartOfCardStack && isVertical && "px-4",
        !isPartOfCardStack && !isVertical && "mx-4",
        params?.Styles,
      ),
    [isVertical, params?.Styles, hiddenBlockStyles, isPartOfCardStack],
  );

  if (!datasource) return <CardDefaultComponent {...props} />;

  const hasVideo = !!datasource?.YoutubeVideoUrl?.jsonValue?.value;
  const hasImage = !!datasource?.Image?.jsonValue?.value?.src;
  const editModeBasedClasses = isPageEditing ? "" : "[&:empty]:hidden";
  const videoUrl = datasource?.ImageUrl?.jsonValue?.value?.href;

  const contentWrapperClass = cx(
    "flex flex-col pb-5 h-full",
    isVertical &&
      "flex-col-reverse lg:flex-row-reverse lg:py-6 lg:gap-x-8 px-4",
  );

  const mediaWrapperClass = cx(
    isPageEditing ? "flex-col" : "[&:empty]:hidden",
    isVertical && "lg:max-w-1/2 w-full lg:items-start",
  );

  const textAreaClass = cx(
    isVertical
      ? cx((hasVideo || hasImage) && "lg:max-w-[calc(50%-16px)]")
      : "justify-between",
    "wrap-anywhere flex flex-col gap-y-6 flex-grow-1",
    !isVertical && "lg:py-[25px]",
  );

  const getIsModalOpen = () => isModalOpen;

  const openModal = () => {
    if (!isModalOpen) {
      setTimeout(() => setIsModalOpen(true), 10);
    }
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderThumbnailImage = (
    width: number,
    height: number,
    className = "",
    onClick?: () => void,
  ) => (
    <JSSImage
      field={datasource?.Image?.jsonValue}
      className={`w-auto max-w-full h-auto max-h-full flex-auto ${
        isVertical ? "" : "rounded-md"
      } ${className}`}
      width={width}
      height={height}
      imageParams={{ mw: width, mh: height }}
      {...(onClick ? { onClick } : {})}
      {...(onClick
        ? {
            role: "button",
            tabIndex: 0,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    />
  );

  const renderMediaDesktop = () => (
    <div
      className={`${mediaWrapperClass} max-lg:hidden lg:flex ${videoUrl ? "cursor-pointer" : ""}`}
    >
      {isPageEditing && datasource?.ImageUrl && (
        <Link field={datasource.ImageUrl?.jsonValue} className="mb-5" />
      )}

      {!datasource?.ImageUrl?.jsonValue?.value?.href || isPageEditing ? (
        renderThumbnailImage(670, 670)
      ) : (
        <Link
          field={datasource?.ImageUrl?.jsonValue}
          tabIndex={-1}
          onClick={(e) => e.preventDefault()}
          className="flex lg:items-start"
        >
          {renderThumbnailImage(670, 670, "cursor-pointer", openModal)}
        </Link>
      )}
    </div>
  );

  const renderMediaMobile = () => (
    <div className={`${mediaWrapperClass} flex lg:hidden`}>
      {isPageEditing && datasource?.ImageUrl && (
        <Link field={datasource.ImageUrl?.jsonValue} className="mb-5" />
      )}

      {!datasource?.ImageUrl?.jsonValue?.value?.href || isPageEditing ? (
        renderThumbnailImage(440, 440)
      ) : (
        <Link field={datasource?.ImageUrl?.jsonValue} className="flex">
          {renderThumbnailImage(440, 440, "cursor-pointer")}
        </Link>
      )}
    </div>
  );

  const renderContent = () => {
    const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
    const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase();

    const titleClass = cx(
      'font-["Special_Gothic_Condensed_One","Roboto"] tracking-[0.4px] lg:tracking-[0.8px] font-medium [&_*]:font-medium mb-5',
      !isVertical &&
        `${
          typeOfTitleTag
            ? getConfiguredHeadingStyles(typeOfTitleTag)
            : "text-[72px] [&_*]:text-[72px] lg:leading-[0.8] lg:[&_*]:leading-[0.8]"
        }  text-medium-blue`,
      isVertical &&
        `[text-shadow:0_0_.7em_#fff] lg:text-shadow-white-off   tracking-[0.4px] lg:tracking-[0.8px] md:[&:first-child]:mt-0 ${
          typeOfTitleTag
            ? getConfiguredHeadingStyles(typeOfTitleTag)
            : "text-[56px] [&_*]:text-[56px] lg:text-[96px] lg:[&_*]:text-[96px] leading-[0.8] [&_*]:leading-[0.8]"
        }`,
      !isDarkBg && "text-ecolab-blue",
    );

    const subTitleClass = cx(
      "font-medium [&_*]:font-medium",
      isDarkBg && "text-white",
      isVertical
        ? `${getConfiguredHeadingStyles(typeOfSubtitleTag || "h4")}`
        : "",
      !isVertical && typeOfSubtitleTag
        ? getConfiguredHeadingStyles(typeOfSubtitleTag)
        : "text-[32px] [&_*]:text-[32px] leading-[1.1] [&_*]:leading-[1.1]",
    );

    const descriptionClass = cx(
      "richtext-content text-base tracking-[0.4px] lg:tracking-[0.8px] leading-[1.5]",
      isDarkBg && "text-white",
      isVertical ? "text-[20px]" : "text-[24px]",
    );

    return (
      <div>
        <div
          className={`flex flex-col ${isVertical ? "mb-2" : "flex-col-reverse"}`}
        >
          <RichTextWithTable
            field={datasource?.Title?.jsonValue}
            className={titleClass}
            tag={typeOfTitleTag ?? "h2"}
          />
          {renderMediaMobile()}
        </div>
        <RichTextWithTable
          field={datasource?.SubTitle?.jsonValue}
          className={subTitleClass}
          tag={typeOfSubtitleTag ?? "h4"}
        />
        <RichTextWithTable
          field={datasource?.Description?.jsonValue}
          className={descriptionClass}
        />
      </div>
    );
  };

  return (
    <div
      id={id ?? undefined}
      className={wrapperClass}
      data-testid={`card-item-${params?.index ?? 0}`}
      data-role="card-component"
    >
      <div className={`flex flex-col pb-5 h-full ${contentWrapperClass}`}>
        {renderMediaDesktop()}

        <div className={`${textAreaClass} flex flex-col gap-y-6 grow`}>
          {renderContent()}
          <div
            className={`flex flex-wrap gap-y-4 items-start max-md:pb-6 ${
              isDarkBg ? "dark-image-bg" : ""
            } ${editModeBasedClasses}`}
          >
            {renderCTAButton(datasource?.PrimaryCTA?.jsonValue, "primary", {
              isPageEditing,
              isAllowReverseStyling: true,
              isVertical,
              isDarkBg,
            })}
            {renderCTAButton(datasource?.SecondaryCTA?.jsonValue, "secondary", {
              isPageEditing,
              isAllowReverseStyling: true,
              isVertical,
              isDarkBg,
            })}
          </div>
        </div>
      </div>
      {videoUrl && (
        <CardVideoModal
          onClose={onCloseModal}
          getIsModalOpen={getIsModalOpen}
          videoUrl={videoUrl}
          isPreviewMode={isPreviewMode}
        />
      )}
    </div>
  );
};
